"use client";

import { LeavePolicyType } from "@gettactic/api";
import { DateRange } from "@gettactic/components/src/calendar/types";
import { SelectItem } from "@gettactic/components/src/form/UnifiedSelect/types";
import { HookFormCalendarInput } from "@gettactic/components/src/form/FormCalendarInput/HookFormCalendarInput";
import { HookFormHiddenInput } from "@gettactic/components/src/form/HiddenInput/HookFormHiddenInput";
import { HookFormTextareaInput } from "@gettactic/components/src/form/TextareaInput/HookFormTextareaInput";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput/HookFormUnifiedSelectInput";
import { CrudModal } from "@gettactic/components/src/overlay/modals/CrudModal/CrudModal";
import { Stack } from "@gettactic/components/src/primitives/Stack";
import { cn } from "@gettactic/helpers/src/css/cn";
import { useWindowSize } from "@gettactic/helpers/src/hooks/useWindowSize";
import { useAuthenticated } from "@gettactic/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { OutOfOfficeOverlapWarning } from "@/features/dashboard/components/attendance/employee/form/OutOfOfficeOverlapWarning";
import { useAllApplicablePolicies } from "@/features/reserve/hooks/useApplicablePolicies";
import { api } from "@/lib/api/api";
import { parseApiDate } from "@/lib/utils";

export type OutOfOfficeRequestData = {
  date: string;
  type: "ooo" | "remote";
} | null;

type Props = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  request: OutOfOfficeRequestData;
};

type Form = {
  type: string;
  reason: string;
  dates: DateRange | undefined;
  multipleDates: Date[];
};

type PolicyOption = {
  label: string;
  value: string;
  available: number | null;
  is_approvable: boolean;
};

function getDefaults(
  request: OutOfOfficeRequestData,
  optionsForPolicyType: { value: string }[]
): Form {
  const isRemote = request?.type !== "ooo";
  const defaultDate = request?.date ? parseApiDate(request?.date) : new Date();
  return {
    type: !isRemote
      ? optionsForPolicyType.length > 0
        ? optionsForPolicyType[0].value
        : ""
      : "remote",
    reason: "",
    dates: {
      from: defaultDate,
      to: defaultDate
    },
    multipleDates: isRemote ? [defaultDate] : []
  };
}

const AvailableLabel = ({ available }: { available: number | null }) => (
  <span
    className={cn(
      available === null || available >= 1
        ? "bg-green-100 text-green-800"
        : "bg-red-100 text-red-800",
      "ml-2 rounded-full px-2.5 py-0.5 font-medium"
    )}
  >
    {available === null ? "unlimited" : `${available} available`}
  </span>
);

export function OutOfOfficeRequest({ isOpen, onOpenChange, request }: Props) {
  const {
    userContext: { deviceTz }
  } = useAuthenticated();
  const queryClient = useQueryClient();
  const browserWindowSize = useWindowSize();
  const browserHeight = browserWindowSize.height;
  const isRemote = request?.type === "remote";

  const form = useForm<Form>({
    defaultValues: getDefaults(request, [])
  });

  const { reset, watch } = form;

  const maxLength = 500;

  // The dashboard keeps this dialog mounted while closed; loading the policies
  // only once it opens saves a request per policy type on every page load.
  const { query: policiesQuery, invalidate } = useAllApplicablePolicies(
    { tz: deviceTz },
    { keepPreviousData: true, enabled: isOpen }
  );

  const dates = watch("dates");

  const optionsForPolicyType = useMemo(() => {
    return policiesQuery.data
      ? policiesQuery.data
          .filter(
            (x) =>
              x.policy.policy_type !== "remote" &&
              x.policy.policy_type !== "office"
          )
          .map((x) => ({
            label: x.policy.title ? x.policy.title : x.policy.policy_type,
            value: x.policy.policy_type,
            available: x.available,
            is_approvable: x.policy.is_approvable
          }))
      : [];
  }, [policiesQuery.data]);

  const selectItems: SelectItem<PolicyOption>[] = useMemo(
    () =>
      optionsForPolicyType.map((opt) => ({
        id: opt.value,
        name: opt.label,
        disabled: opt.available !== null && opt.available <= 0,
        data: opt
      })),
    [optionsForPolicyType]
  );

  useEffect(() => {
    reset(getDefaults(request, []));
  }, [request, reset]);

  // The policies load after the dialog opens (and refetch after a submit), so
  // only fill in a type the user hasn't picked instead of resetting the form.
  useEffect(() => {
    if (request?.type !== "ooo" || optionsForPolicyType.length === 0) {
      return;
    }
    if (!form.getValues("type")) {
      form.setValue("type", optionsForPolicyType[0].value);
    }
  }, [request, optionsForPolicyType, form]);

  const handleSubmit = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const data = form.getValues();
    try {
      if (!isRemote) {
        const startDate = data.dates?.from ?? new Date();
        const endDate = data.dates?.to ?? startDate;
        await api.client.leave.createRequest({
          policy_type: data.type as LeavePolicyType,
          reason: data.reason,
          starts: `${format(startDate, "yyyy-MM-dd")}T00:00:00.000Z`,
          ends: `${format(endDate, "yyyy-MM-dd")}T23:59:59.000Z`,
          time_zone_id: deviceTz
        });
        const isApprovable =
          optionsForPolicyType.find((x) => x.value === data.type)
            ?.is_approvable ?? false;
        toast.success(
          !isApprovable
            ? "Your request was submitted"
            : "Your organization's policies require your request to be approved. Once approved it will display here."
        );
      } else {
        const all = data.multipleDates.map(async (date) => {
          await api.client.leave.createRequest({
            policy_type: data.type as LeavePolicyType,
            reason: data.reason,
            starts: `${format(date, "yyyy-MM-dd")}T00:00:00.000Z`,
            ends: `${format(date, "yyyy-MM-dd")}T23:59:59.000Z`,
            time_zone_id: deviceTz
          });
        });
        await Promise.all(all);
        toast.success(
          "Your organization's Remote Work policy require your request to be approved."
        );
      }
      onOpenChange(false);
    } catch {
      toast.error("Something went wrong");
    }
    await invalidate();
    await queryClient.invalidateQueries({
      queryKey: ["schedules"]
    });
    await queryClient.invalidateQueries({
      queryKey: ["myAgenda"]
    });
    await queryClient.invalidateQueries({
      queryKey: ["attendanceRequests"]
    });
    await queryClient.invalidateQueries({
      queryKey: ["leavePolicies"]
    });
    await queryClient.invalidateQueries({
      queryKey: ["tableOverview"]
    });
  };

  const title = isRemote ? "Remote Day Request" : "Time-Off Request";

  return (
    <CrudModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={title}
      form={form}
      onSubmit={handleSubmit}
      submitLabel="Submit Request"
      confirmWhenDirty={false}
      size="xs"
    >
      <Stack
        className="gap-4 pt-4"
        style={{
          maxHeight: browserHeight ? `${browserHeight - 200}px` : "28rem"
        }}
      >
        {isRemote ? (
          <HookFormHiddenInput form={form} name="type" />
        ) : (
          <HookFormUnifiedSelectInput<Form, PolicyOption>
            form={form}
            name="type"
            label="Type"
            items={selectItems}
            isRequired
            placeholder="Select type..."
            triggerClassName="w-full"
            renderItem={(item) => (
              <div className="flex items-center">
                <span className="capitalize">{item.name}</span>
                <AvailableLabel available={item.data?.available ?? null} />
              </div>
            )}
            renderValue={(selected) => {
              if (!selected || Array.isArray(selected)) return null;
              return (
                <div className="flex items-center">
                  <span className="capitalize">{selected.name}</span>
                  <AvailableLabel
                    available={selected.data?.available ?? null}
                  />
                </div>
              );
            }}
          />
        )}

        {isRemote ? (
          <HookFormCalendarInput<Form>
            form={form}
            name="multipleDates"
            label="Dates"
            mode="multiple"
            variant="outlined"
            isRequired
            setValueOnApply
          />
        ) : (
          <HookFormCalendarInput<Form>
            form={form}
            name="dates"
            label="Dates"
            mode="range"
            variant="outlined"
            isRequired
            setValueOnApply
          />
        )}

        {!isRemote && <OutOfOfficeOverlapWarning dates={dates} />}

        <HookFormTextareaInput
          form={form}
          name="reason"
          label="Reason"
          rows={4}
          placeholder="Reason for request (optional)..."
          rules={{ maxLength }}
          maxLength={maxLength}
        />
      </Stack>
    </CrudModal>
  );
}
